import { Box, Select, TextInput, ActionIcon, MultiSelect } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { SurveyLogicCondition, SurveyLogicElement } from '@vision/ui/interfaces';
import { memo, useCallback } from 'react';
import { TextWithEmoji, VMultiSelect } from '@vision/ui/components';
import { useConditionBuilder } from './hooks';
import { ConditionValueToken } from '../ConditionValueToken';
import classes from './ConditionBuilder.module.css';

interface ConditionBuilderProps {
  ruleId: string;
  groupId: string;
  condition: SurveyLogicCondition;
  conditionIndex: number;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
  isLast: boolean;
}

export const ConditionBuilder = memo(function ConditionBuilder({
  ruleId,
  groupId,
  condition,
  conditionIndex,
  elements,
  activeLanguageCode,
  isLast,
}: ConditionBuilderProps) {
  const {
    t,
    tCommon,
    elementOptions,
    optionOptions,
    statementOptions,
    textRuleTypeOptions,
    selectedElement,
    selectedOptionKeys,
    selectedStatementKeys,
    isMatrixElement,
    isTextElement,
    showOptionSelect,
    optionKeysOperator,
    localAnswerValue,
    setLocalAnswerValue,
    getOptionLabel,
    handleElementChange,
    handleStatementKeysChange,
    handleRuleTypeChange,
    handleOptionKeysChange,
    handleRemoveOption,
    handleOptionKeysOperatorToggle,
    handleAnswerValueBlur,
    handleDelete,
    handleAddCondition,
  } = useConditionBuilder({ ruleId, groupId, condition, elements, activeLanguageCode });

  const renderSelectOption = useCallback(
    ({ option }: { option: { value: string; label: string } }) => <TextWithEmoji text={option.label} />,
    [],
  );

  return (
    <Box className={classes.container} data-testid={`section-condition-${conditionIndex}`}>
      <Box className={classes.conditionRow}>
        <Box className={classes.questionLabel}>
          <Select
            value="question"
            data={[{ value: 'question', label: t('conditionBuilder.question') }]}
            readOnly={true}
            className={classes.questionLabelSelect}
            data-testid={`select-condition-label-${conditionIndex}`}
          />
        </Box>

        <Box className={classes.questionSelect}>
          <Select
            value={condition.elementKey || null}
            onChange={handleElementChange}
            data={elementOptions}
            placeholder={t('conditionBuilder.selectQuestion')}
            searchable={true}
            clearable={true}
            renderOption={renderSelectOption}
            data-testid={`select-condition-element-${conditionIndex}`}
          />
        </Box>

        {isMatrixElement && (
          <Box className={classes.optionSelectSmall}>
            <VMultiSelect
              value={selectedStatementKeys}
              onChange={handleStatementKeysChange}
              data={statementOptions}
              placeholder={t('conditionBuilder.selectStatement')}
              searchable={true}
              clearable={true}
              showSelectedPillCountText={true}
              maxVisibleItems={0}
              withinPortal={true}
              data-testid={`select-condition-statement-${conditionIndex}`}
            />
          </Box>
        )}

        {isTextElement && (
          <>
            <Box className={classes.optionSelectSmall}>
              <Select
                value={condition.ruleType}
                onChange={handleRuleTypeChange}
                data={textRuleTypeOptions}
                data-testid={`select-condition-rule-type-${conditionIndex}`}
              />
            </Box>
            <Box className={classes.valueInput}>
              <TextInput
                value={localAnswerValue}
                onChange={(e) => setLocalAnswerValue(e.target.value)}
                onBlur={handleAnswerValueBlur}
                placeholder={t('conditionBuilder.selectValue')}
                data-testid={`input-condition-value-${conditionIndex}`}
              />
            </Box>
          </>
        )}

        {showOptionSelect && (
          <Box className={classes.optionSelectSmall}>
            <MultiSelect
              className={classes.optionMultiSelect}
              value={selectedOptionKeys}
              onChange={handleOptionKeysChange}
              data={optionOptions}
              placeholder={t('conditionBuilder.selectValue')}
              searchable={true}
              clearable={true}
              hidePickedOptions={true}
              disabled={!selectedElement}
              renderOption={renderSelectOption}
              data-testid={`select-condition-options-${conditionIndex}`}
            />
          </Box>
        )}

        <Box className={classes.actionButtons}>
          {isLast && (
            <ActionIcon
              className={classes.addButton}
              onClick={handleAddCondition}
              data-testid={`button-add-condition-${conditionIndex}`}
            >
              <FontAwesomeIcon icon={faCirclePlus} />
            </ActionIcon>
          )}

          <ActionIcon
            className={classes.deleteButton}
            onClick={handleDelete}
            style={{ visibility: conditionIndex === 0 && isLast ? 'hidden' : 'visible' }}
            data-testid={`button-delete-condition-${conditionIndex}`}
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </ActionIcon>
        </Box>
      </Box>

      <Box className={classes.selectedOptionsContainer}>
        <Box className={classes.selectedOptions}>
          {selectedOptionKeys.map((optionKey, idx) => (
            <ConditionValueToken
              key={optionKey}
              value={getOptionLabel(optionKey)}
              valueTooltipLabel={tCommon('delete')}
              onValueClick={() => handleRemoveOption(optionKey)}
              operator={idx > 0 ? optionKeysOperator : undefined}
              operatorLabel={idx > 0 ? t(`operator.${optionKeysOperator}`) : undefined}
              onOperatorClick={idx > 0 ? handleOptionKeysOperatorToggle : undefined}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
});
